import _ from 'lodash'
import { intervalToDuration, addDays, addMonths } from 'date-fns'
import {
  COLUMN_MODEL,
  COLUMN_NAME,
  COLUMN_SN,
  COLUMN_ASSOCIATED_APPS,
  COLUMN_LICENSE_ACTIVATED,
  MONTH,
  DAY,
  EVAL,
  TRIAL,
  HUMAN_READABLE_DEVICE_LICENSE_TYPE,
  COLUMN_ASSET_TYPE,
} from '../../../constants/LabelConstants'
import SerialNumberRenderer from '../../common/table/cellRenderer/SerialNumberRenderer'
import CellRenderer from '../../common/table/cellRenderer/CellRenderer'
import AppsRenderer from '../../common/table/cellRenderer/AppsRenderer'
import { ARRAY } from '../../../../common/utils/Utils'
import DeviceAssetTypeRenderer from '../../common/table/cellRenderer/DeviceAssetTypeRenderer'
import LicenseActivatedRenderer from '../../common/table/cellRenderer/LicenseActivatedRenderer'
import { ELA } from '../../../../common/constants/GlobalConstants'

export const tableColumns = (hasFirewallLicense, selectedApp) => {
  if (hasFirewallLicense) {
    return [
      {
        accessor: 'serial_number',
        Header: COLUMN_SN,
        width: 180,
        id: 'serial_number',
        Cell: SerialNumberRenderer,
      },
      {
        accessor: 'device_name',
        Header: COLUMN_NAME,
        width: 150,
        id: 'device_name',
        Cell: CellRenderer
      },
      {
        accessor: 'model',
        Header: COLUMN_MODEL,
        width: 120,
        id: 'model',
        Cell: CellRenderer,
      },
      {
        accessor: 'asset_type',
        Header: COLUMN_ASSET_TYPE,
        width: 100,
        id: 'asset_type',
        Cell: DeviceAssetTypeRenderer,
      },
      {
        accessor: 'license_activated',
        Header: COLUMN_LICENSE_ACTIVATED,
        width: 120,
        id: 'license_activated',
        Cell: ({ row }) => LicenseActivatedRenderer({ row, selectedApp }),
      },
    ]
  }
  else {
    return [
      {
        accessor: 'serial_number',
        Header: COLUMN_SN,
        width: 200,
        id: 'serial_number',
        Cell: SerialNumberRenderer,
        cellToString: ({ row }) => row?.original.serial_number,
      },
      {
        accessor: 'device_name',
        Header: COLUMN_NAME,
        width: 150,
        id: 'device_name',
        Cell: CellRenderer
      },
      {
        accessor: 'model',
        Header: COLUMN_MODEL,
        width: 120,
        id: 'model',
        Cell: CellRenderer,
      },
      {
        accessor: 'associations',
        Header: COLUMN_ASSOCIATED_APPS,
        width: 180,
        id: 'associations',
        Cell: AppsRenderer,
        cellToString: ({ row }) => row?.original?.associations?.map(({ display_name }) => display_name).join(' '),
      },
    ]
  }
}

export const trimFirewallLicenseSku = (licenseSku) => {
  //Hardware series: License SKU: PAN-PA-200-AIOPS-NGFW, device_model: PAN-PA-200
  //Hardware series special case: License SKU: PAN-PA-200R-AIOPS-NGFW, device_model: PAN-PA-200R
  //VM series: License SKU: PAN-VM-300-**, device_model: PA-VM-300
  //VM panorama series: License SKU: PAN-PRA-1000-AIOPS-NGFW, device_model: PRA-1000
  //panorama series: License SKU: PAN-M-600-AIOPS-NGFW, device_model: PA-M-600
  //5G series: License SKU: PAN-PA-415-AIOPS-NGFW, device_model: PA-415-5G
  //5G series: License SKU: PAN-PA-450R-5G-IOT-DRDL-3YR, device_model: PA-450R-5G
  return licenseSku?.match(/PA-(\d+)(R-5G|r|R|-5G)?/) || licenseSku?.match(/VM-(\d+)/) || licenseSku?.match(/PRA-(\d+)/) || licenseSku?.match(/M-(\d+)/)
}

/**
 *
 * @param {number} term
 * @param {string} unit
 */
export const getRemainingTime = (term, unit) => {
  let result = ''
  if (unit !== MONTH && unit !== DAY) {
    return term //invalid unit, return as is
  }
  const start = new Date()
  let end
  if (unit === MONTH) {
    end = addMonths(start, +term)
  }
  else if (unit === DAY) {
    end = addDays(start, +term)
  }
  const { years, months, days } = intervalToDuration({ start, end }) || {}
  if (years > 0) {
    result = years > 1 ? `${years} years` : `${years} year`
  }

  if (months > 0) {
    result = `${result} ${months}`
    result = months > 1 ? `${result} months` : `${result} month`
  }

  if (unit === MONTH && days > 0) {
    result = '1 month' //APPORTAL-4005: if unit is month ceil remaining days always to 1 month (eg: jan-29 to feb-28)
  }

  if (unit === DAY && days > 0) { // only if request unit in days
    result = `${result} ${days}`
    result = days > 1 ? `${result} days` : `${result} day`
  }
  return result
}

/**
 *
 * @param {string} deviceModel
 * @param {string} sku
 */
//TODO - currently below is not used anywhere, check we need this during saas inline flows
export const firewallModelMatch = (deviceModel, sku) => {
  let model = deviceModel

  const modelUpCase = deviceModel?.toUpperCase()
  //VM series special case: SKU: PAN-VM-300-**, device_model: PA-VM-300
  //panorama series special case: SKU: PAN-M-600-AIOPS-NGFW, device_model: PA-M-600
  //VM panorama series special case: SKU: PAN-PRA-1000-AIOPS-NGFW, device_model: PRA-1000
  const value = modelUpCase?.match(/-VM-(\d+)/) || modelUpCase?.match(/-PRA-(\d+)/) || modelUpCase?.match(/-M-(\d+)/)

  if (value) {
    model = value?.[0]
  }
  return sku?.match(model)
}

//TODO - currently below is not used anywhere, check we need this during saas inline flows
export const modifyDevicesData = (deviceData) => {
  const clonedDeviceData = _.cloneDeep(deviceData)
  return clonedDeviceData.map(device => {
    // includes: PAN-PA-XX-EMP || PAN-PA-VM-XX-E30,  excludes: PAN-PA-200E
    const trimModel = device?.model?.match(/^(PA-\d+-)|(PA-VM-\d+-)|(PAN-PRA-\d+-)/g)
    if (trimModel) {
      const str = trimModel[0]
      device.model = str.replace(/-$/, '') //remove trailing hyphen
    }
    return device
  })
}


export const selectedAppDef = (appsDefMap, selectedApp) => appsDefMap[selectedApp?.app_id]

export const filterDevicesWithRegex = (appDeviceRegex, devices) => {
  const matched = appDeviceRegex.match(/\/(.+)\/i?/)
  if (matched) {
    const ngfwRegex = new RegExp(matched[1], 'i')
    return devices.filter(device => {
      // no longer needed since BE is setting model for VM panorama
      // // due to IT inconsistency, special logic for "PRA" panorama's (VM's), regex on SKU for "PRA" panorama's (VM's)
      // if (device?.sku.includes('-PRA-')) {
      //   return ngfwRegex.test(device?.model)
      // }
      return ngfwRegex.test(device?.model)
    })
  }
  return [] // regex fails, default it to empty
}

export const isAgnosticLicense = (product_type) => [EVAL, TRIAL].includes(product_type)

export const isADEMX = (extra) => extra?.license_version && (extra?.license_version.toUpperCase() === 'GP' || extra?.license_version.toUpperCase() === 'GP-SDWAN')

export const buildFirewallLicenseGrouping = (appFirewallLicenses) => {
  const clonedAppFirewallLicenses = _.cloneDeep(appFirewallLicenses)
  // const sanitizeLicenseData = clonedAppFirewallLicenses?.map(each => {
  //   const { sku, auth_code, is_activated, license_id } = each
  //   const { license_term, license_unit, product_type } = each?.entitlements[0]
  //   const { license_version = '1.0' } = each?.extra || {}
  //   return
  // })

  const LicenseSkuGrouping = clonedAppFirewallLicenses?.reduce((grouping, each) => {
    const { sku, auth_code, is_activated, license_id, entitlements = [], extra } = each
    if (!is_activated) {
      const { license_term, license_unit, product_type } = entitlements[0]
      const { license_version, is_ela, serial_number: raptorSerialNumber } = extra || {}

      // for eval or trail or ELA there is no model number in license and can apply on any device model, hence NO license_sku_model
      const isModelAgnostic = isAgnosticLicense(product_type) || is_ela || isADEMX(extra)
      const license_sku_model = !isModelAgnostic ? trimFirewallLicenseSku(sku)?.[0] : null
      const license_duration = getRemainingTime(license_term, license_unit)

      const item = {
        license_sku_model,
        product_type,
        license_duration,
        license_term,
        license_unit,
        license_version,
        auth_code,
        is_activated,
        license_id,
        isModelAgnostic,
        isEla: is_ela,
        raptorSerialNumber, // required for raptor bundles (iot, saas-inline)
        license_sku: sku // for eg, PAN-PA-1410-NET-ADEM-OB-5YR
      }
      const key = ARRAY.clean([license_sku_model, product_type, license_term, license_unit, license_version, raptorSerialNumber]).join('-')
      if (grouping.has(key)) {
        const existingList = grouping.get(key)
        existingList.push(item)
        grouping.set(key, existingList)
      }
      else {
        grouping.set(key, [item])
      }
    }
    return grouping
  }, new Map())

  return LicenseSkuGrouping
}

// calculate device-license matrix compatibility
const computeDeviceLicenseCompatible = ({ device, selectedApp, device_license_matrix, product_type }) => {
  const { device_tenant_id, extra = {} } = device?.associations?.find(assoc => assoc.app_id === selectedApp?.app_id) || {}
  const deviceAssetType = device?.asset_type?.toUpperCase()
  const newLicenseType = product_type?.toUpperCase()
  let result = { isDeviceCompatible: false }
  if (device_tenant_id) { // upgrade
    const { license_type } = extra
    const currentLicenseType = license_type?.toUpperCase()
    const initialLicenseType = device_license_matrix.upgrade.initial[deviceAssetType]
    const updateEligibleLicenseType = device_license_matrix.upgrade.update[deviceAssetType]
    if (initialLicenseType?.includes(currentLicenseType) && updateEligibleLicenseType?.includes(newLicenseType)) {
      result = {
        isDeviceCompatible: true,
        isUpgrade: true
      }
    }
  }
  else { //net new
    const eligibleLicenseTypes = device_license_matrix.new[deviceAssetType]
    if (eligibleLicenseTypes?.includes(newLicenseType)) {
      result = {
        isDeviceCompatible: true,
        isUpgrade: false
      }
    }
  }
  return result
}

export const filterDevicesWithLicenseGroup = ({ appDef, selectedApp, devices, licenseModelTypeTermMap, currentSelectedLicense, skuCompatibleDeviceMap, deviceLicensesMap }) => {
  const { model_regex: appDeviceRegex, device_license_matrix, use_device_matrix_api } = appDef?.device_association

  if (use_device_matrix_api) {
    const { license_sku: currentSelectedLicenseSku, license_version, product_type } = licenseModelTypeTermMap.get(currentSelectedLicense.id)[0] || {}
    let filteredDevices = devices?.filter(e => (_.isEmpty(e?.associations) || e?.associations.every(assoc => assoc.app_id !== selectedApp?.app_id)))
    if (!isADEMX({ license_version })) {
      const skuList = skuCompatibleDeviceMap?.get(currentSelectedLicenseSku)
      filteredDevices = filteredDevices?.filter(e => skuList?.includes(e.sku))
    }
    return filteredDevices
    // return filteredDevices.filter(device => {
    //   const { isUpgrade, isDeviceCompatible } = computeDeviceLicenseCompatible({ device, selectedApp, device_license_matrix, product_type })
    //   if (isDeviceCompatible) {
    //     device.isUpgrade = isUpgrade //track device upgrade
    //     return true
    //   }
    //   return false
    // })
    // return filterDeviceWithBaseLicense({ appDef, filteredDevices, license_version })
  }
  if (appDeviceRegex) {
    //filter devices based on selectedItem.id (PA-220-prod-24-month) regex for model
    const { license_sku_model, product_type, isModelAgnostic, isEla, raptorSerialNumber } = licenseModelTypeTermMap?.get(currentSelectedLicense?.id)?.[0]

    // filter app support device models
    const filteredDevices = filterDevicesWithRegex(appDeviceRegex, devices)
    if (isEla) { // ELA has no compatible matrix restriction
      return filteredDevices.filter(e => _.isEmpty(e?.associations) || e?.associations.every(assoc => assoc.app_id !== selectedApp?.app_id)) // filter out devices if it's already associated with selected app
    }
    else if (raptorSerialNumber) {
      return filteredDevices.filter(e => (_.isEmpty(e?.associations) || e?.associations.every(assoc => assoc.app_id !== selectedApp?.app_id)) && e.serial_number === raptorSerialNumber) // filter out devices if it's already associated with selected app + matching license device serial number
    }
    else {
      return filteredDevices.filter(device => {
        // run compatible matrix check - for EVAL/TRAIL license (OR) for others licenses only if model<=>license match (OR) VM series special case: SKU: PAN-VM-300-**, device_model: PA-VM-300 (OR) M series special case: SKU: PAN-M-200-**, device_model: PA-M-200
        //TODO IOT-VM: needed for IoT flows and remove VM once VM flex is released
        if (isModelAgnostic || (device.model === license_sku_model) || (device?.model?.match(/VM-(\d+)/)?.[0] === license_sku_model) || (device?.model?.match(/M-(\d+)/)?.[0] === license_sku_model)) {
          const { isUpgrade, isDeviceCompatible } = computeDeviceLicenseCompatible({ device, selectedApp, device_license_matrix, product_type })
          if (isDeviceCompatible) {
            device.isUpgrade = isUpgrade //track device upgrade
            return true
          }
        }
        return false
      })
    }
  }
  // TODO for now default to devices, once "selectedApp" in sync with paid apps state fields (resetPaidAppsLocalStates), we return default all devices
  return devices

}

export const filterDeviceWithBaseLicense = ({ appDef, devices, license_version }) => {
  return devices.filter(e => e?.licenses?.filter((l) => l?.licensePanDbIdentification === license_version)) //
}
export const constructSelectItems = ({ licenseModelTypeTermMap, appDef }) => {
  const { license_version: app_license_version } = appDef?.device_association
  const selectItems = []
  for (const [key, value] of licenseModelTypeTermMap) {
    const licenseCount = value?.length
    // pick first item since its grouped based ob below properties
    const { license_sku_model, product_type, license_duration, isModelAgnostic, isEla, license_version, raptorSerialNumber } = value[0]
    let subLabel = `${HUMAN_READABLE_DEVICE_LICENSE_TYPE[product_type] || _.capitalize(product_type)}, ${license_duration}`
    if (!_.isEmpty(app_license_version?.[license_version]?.display_name)) {
      subLabel = `${subLabel}, ${app_license_version[license_version].display_name}`
    }
    let customLabel = ''
    if (isEla) { // ela has unlimited licenses hence ignore the count
      customLabel = `${ELA}: ${subLabel}`
    }
    else {
      if (!isModelAgnostic) {
        customLabel = `${license_sku_model}: `
      }
      customLabel += `${subLabel} (${licenseCount} available licenses)`

      if (raptorSerialNumber) {
        customLabel += ' (raptor)'
      }
    }

    selectItems.push({
      id: key,
      value: customLabel,
      children: customLabel
    })
  }
  return selectItems
}

export const isFirewallRequireLicense = (apps, selectedApp) => Object.values(apps)?.find(app => app.app_id === selectedApp?.app_id)?.device_association?.license

export const hasSelectedAppHasLicenseFlavor = (apps, selectedApp) => Object.values(apps)?.find(app => app.app_id === selectedApp?.app_id)?.device_association?.has_license_flavor === true

export const buildSkuCompatibleDevicelist = (skuCompatibleDeviceResponse) => {
  const deviceMap = new Map()
  skuCompatibleDeviceResponse?.forEach(mainSku => {
    if (!deviceMap.has(mainSku?.main_part_number)) {
      deviceMap.set(mainSku?.main_part_number, mainSku?.compatible_parts || [])
    }
  })
  return deviceMap
}
export const buildDeviceLicenselist = (deviceLicenseResponse) => {
  const deviceLicenseMap = new Map()
  deviceLicenseResponse?.forEach(device => {
    if (!deviceLicenseMap.has(device?.serialNumber)) {
      deviceLicenseMap.set(device?.serialNumber, device?.licenses || [])
    }
  })
  return deviceLicenseMap
}

export const filterDeviceWithSCM = (selectedApp, devices) => {
  const notAssociatedDevice = devices?.filter(e => _.isEmpty(e?.associations) || e?.associations.every(assoc => assoc.app_id !== selectedApp?.app_id))
  const associatedDevice = devices?.filter(e => !_.isEmpty(e?.associations) && e?.associations.some(assoc => assoc.app_id === selectedApp?.app_id))
  return { associatedDevice, notAssociatedDevice }
}
export const calculateAvailableDevicesCount = (associatedDevice, purchased_size) => {
  return associatedDevice?.length > purchased_size ? 0 : (purchased_size - associatedDevice.length)
}
